import { cloneDeep, isNumber, toInteger } from 'lodash';
import { getFramework } from '@/framework';
import {
    MAX_FONT_SIZE,
    MAX_FONT_SIZE_FOR_EDGELESS,
    MIN_FONT_SIZE,
    TextWritingType,
    type ICorrectLayerMinSize,
    type IFontBackground,
    type IFontGlow,
    type IFontShadow,
    type IFontStroke,
    type ILayerBehavior,
    type ILayerProps,
    type ITextLayerProps,
} from '@/version-types';
import type { TextLayer } from '../domain';
import { applySnapshotSize, requestTextSVGPreview } from '../utils';
import { getTextLayerHost } from '../utils/host';

type Behaviors = Pick<
    ILayerBehavior,
    | 'onBeforeScaleUpdate'
    | 'onBeforeResizeUpdate'
    | 'onBeforeCrop'
    | 'getActiveBaseInfo'
    | 'getScaleMinRatio'
    | 'getLayerMinSize'
    | 'whenGroupCanResize'
    | 'correctLayerMinSize'
    | 'onBeforeGetLayerWithClone'
    | 'onBaseScaleEnd'
    | 'onCheckCanResize'
    | 'scaleOnlyUpdateBaseInfo'
    | 'resizeOnlyUpdateBaseInfo'
>;

export const createOperationBehaviors = (identity: string) => {
    const framework = getFramework(identity);

    const behaviors: Behaviors = {
        /**
         * 文本图层缩放操作后，对文本图层数据的额外操作
         *
         * ATTENTION: 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         * @param options - 配置选项
         */
        onBeforeScaleUpdate(layer: ILayerProps, { correctResult }) {
            const { letterSpacing, textGlow, textShadow, textStroke, textBackground, fontSize } =
                correctResult as ICorrectLayerMinSize;

            const layerData = layer.data as ITextLayerProps;
            const ratio = fontSize / layerData.fontSize;

            layerData.letterSpacing = letterSpacing;
            layerData.fontSize = fontSize;
            layerData.textGlow = textGlow;
            layerData.textShadow = textShadow;
            layerData.textStroke = textStroke;
            layerData.textBackground = textBackground;

            const textContents = layerData.textContents || [];
            if (textContents.length > 0) {
                layerData.textContents = textContents.map((item) => {
                    item.fontSize = item.fontSize * ratio;
                    return item;
                });
            }
        },

        scaleOnlyUpdateBaseInfo() {
            return true;
        },

        resizeOnlyUpdateBaseInfo(layer) {
            requestTextSVGPreview(framework, 'update', layer);
            return true;
        },

        /**
         * 文本图层尺寸调整操作后，根据实际情况（文本内容）返回修正后的图层尺寸
         *
         * ATTENTION: 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         * @param extra - 额外属性
         * @returns 修正后的最小尺寸状态与图层尺寸。
         */
        onBeforeResizeUpdate(layer: ILayerProps, { isChangeHeight }) {
            const layerData = layer.data as ITextLayerProps;
            const isVertical = layerData.writingMode === TextWritingType.VERTICAL_RL;
            if (isVertical !== isChangeHeight) {
                return { isMin: false, width: layer.width, height: layer.height };
            }

            layer.__fixedWidth = true;
            const edgeless = getFramework(identity).service.edgelessCanvas;
            let resolution = getTextLayerHost(edgeless, layer.id)?.layout(layer);
            // Resize 只能应用当前输入的 ready 几何，其他状态继续保留现有尺寸。
            if (resolution?.status !== 'ready') return { isMin: false, width: layer.width, height: layer.height };
            let snapshot = resolution.value;

            const minInlineSize = snapshot.minInlineSize + snapshot.listMarkerIndent;
            const inlineSize = isVertical ? layer.height : layer.width;
            const isMin = inlineSize < minInlineSize;
            if (isMin) {
                if (isVertical) layer.height = minInlineSize;
                else layer.width = minInlineSize;
                resolution = getTextLayerHost(edgeless, layer.id)?.layout(layer);
                if (resolution?.status !== 'ready') return { isMin, width: layer.width, height: layer.height };
                snapshot = resolution.value;
            }

            Object.assign(layer, applySnapshotSize(layer, snapshot));
            return {
                isMin,
                width: layer.width,
                height: layer.height,
            };
        },

        /**
         * 舞台裁切操作后，对文本图层数据的额外操作
         *
         * - 主要对字体尺寸进行调整
         * - 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         * @param scale - 缩放比例
         */
        onBeforeCrop(layer, scale) {
            const data = layer.data as ITextLayerProps;
            const { fontSize } = data;
            data.fontSize = fontSize * scale;
        },

        /**
         * 获取激活的基础信息
         *
         * @param layer - 图层属性
         */
        getActiveBaseInfo(layer: ILayerProps) {
            const layerData = layer.data as ITextLayerProps;
            const {
                fontSize,
                letterSpacing,
                textGlow = [],
                textShadow = [],
                textStroke = [],
                textBackground = [],
            } = layerData;

            return {
                fontSize,
                letterSpacing,
                textGlow: cloneDeep(textGlow),
                textShadow: cloneDeep(textShadow),
                textStroke: cloneDeep(textStroke),
                textBackground: cloneDeep(textBackground),
            };
        },

        /**
         * 获取图层缩放，图层达到最小值的比例
         *
         * @param originInfo- 原始图层信息
         * @param layer - 图层属性
         */
        getScaleMinRatio(originInfo, _) {
            const { fontSize: originFontSize = 6 } = originInfo;
            return MIN_FONT_SIZE / originFontSize;
        },

        /**
         * 获取图层的最小尺寸
         *
         * @param originInfo- 原始图层信息
         * @param layer - 图层属性
         */
        getLayerMinSize(originInfo, _) {
            const { width, height } = originInfo;
            return { width, height };
        },

        whenGroupCanResize(layer: ILayerProps) {
            const layerData = layer.data as ITextLayerProps;
            const { writingMode, shapeExtension } = layerData;

            let horizontal = false;
            let vertical = false;

            if (!shapeExtension) {
                horizontal = writingMode === TextWritingType.HORIZONTAL_TB ? true : false;
                vertical = writingMode === TextWritingType.VERTICAL_RL ? true : false;
            }
            return { horizontal, vertical };
        },

        onCheckCanResize(layer: ILayerProps) {
            const layerData = layer.data as ITextLayerProps;
            const { writingMode, shapeExtension } = layerData;

            let horizontal = false;
            let vertical = false;

            if (!shapeExtension) {
                horizontal = writingMode === TextWritingType.HORIZONTAL_TB ? true : false;
                vertical = writingMode === TextWritingType.VERTICAL_RL ? true : false;
            }
            return { horizontal, vertical };
        },

        correctLayerMinSize(width, height, ratio, originInfo) {
            const {
                width: originWidth,
                height: originHeight,
                fontSize: originFontSize = 6,
                letterSpacing: originLetterSpacing = 0,
                textGlow: originTextGlow = [],
                textShadow: originTextShadow = [],
                textStroke: originTextStroke = [],
                textBackground: originTextBackground = [],
            } = originInfo;

            let fontSize = toInteger(originFontSize * ratio);
            let letterSpacing = originLetterSpacing * ratio;
            let isMin = false;

            const resetGlow = (ratio: number) => {
                return (item: IFontGlow) => {
                    const ret = cloneDeep(item);
                    ret.width = Math.round(item.width * ratio);
                    return ret;
                };
            };

            const resetShadow = (ratio: number) => {
                return (item: IFontShadow) => {
                    const ret = cloneDeep(item);
                    ret.x = Math.round(item.x * ratio);
                    ret.y = Math.round(item.y * ratio);
                    return ret;
                };
            };

            const resetStroke = (ratio: number) => {
                return (item: IFontStroke) => {
                    const ret = cloneDeep(item);
                    ret.width = Math.round(item.width * ratio);
                    return ret;
                };
            };

            const resetBackground = (ratio: number) => {
                return (item: IFontBackground) => {
                    const ret = cloneDeep(item);
                    if (isNumber(item.roundWeight)) {
                        ret.roundWeight = Math.round(item.roundWeight * ratio);
                    }
                    return ret;
                };
            };

            let textGlow = originTextGlow.map(resetGlow(ratio));
            let textShadow = originTextShadow.map(resetShadow(ratio));
            let textStroke = originTextStroke.map(resetStroke(ratio));
            let textBackground = originTextBackground.map(resetBackground(ratio));

            // BUGFIX: https://jira.meitu.com/browse/MTXXPRO-58791
            let minOrMaxRatio: number | undefined = undefined;

            const max = getFramework(identity).service.edgelessCanvas.isEdgelessMode
                ? MAX_FONT_SIZE_FOR_EDGELESS
                : MAX_FONT_SIZE;

            if (fontSize < MIN_FONT_SIZE) {
                fontSize = MIN_FONT_SIZE;
                minOrMaxRatio = fontSize / originFontSize;
                isMin = true;
            } else if (fontSize > max) {
                fontSize = max;
                minOrMaxRatio = fontSize / originFontSize;
            }

            if (typeof minOrMaxRatio !== 'undefined') {
                width = minOrMaxRatio * originWidth;
                height = minOrMaxRatio * originHeight;
                letterSpacing = minOrMaxRatio * originLetterSpacing;
                textGlow = originTextGlow.map(resetGlow(minOrMaxRatio));
                textShadow = originTextShadow.map(resetShadow(minOrMaxRatio));
                textStroke = originTextStroke.map(resetStroke(minOrMaxRatio));
                textBackground = originTextBackground.map(resetBackground(minOrMaxRatio));
            }

            return {
                width: Math.ceil(width),
                height: Math.ceil(height),
                fontSize,
                letterSpacing,
                isMin,
                textGlow,
                textShadow,
                textStroke,
                textBackground,
            };
        },

        onBeforeGetLayerWithClone(layer) {
            delete layer.__isOnTextEditing;
        },

        /**
         * 缩放结束后刷新变形文字截图，或用真实 Text Layer 宿主校准普通文字尺寸。
         *
         * @param layerId - 完成缩放的 Text Layer ID。
         * @returns 完成刷新或校准时返回 true；无需或无法处理时返回 false。
         */
        async onBaseScaleEnd(layerId) {
            // HACK: 文字图层通过比例计算出的尺寸不太准确，需要在缩放结束时通过测量来获取准确的尺寸

            const layer = framework.service.flatLayer.getLayer(layerId, true);
            if (!layer) return false;

            const { shapeExtension } = layer.data as ITextLayerProps;

            if (shapeExtension) {
                const textLayer = framework.service.registration.getInstance<TextLayer>('TextLayer');
                if (!textLayer) return false;

                try {
                    const nextLayer = await textLayer.refreshShapeTextRender(layer);
                    framework.service.frame.addOrUpdateLayer(nextLayer);
                    return true;
                } catch (error) {
                    console.error('Failed to refresh shape text after scale:', error);
                    return false;
                }
            }

            const resolution = getTextLayerHost(framework.service.edgelessCanvas, layer.id)?.layout(layer);
            // 缩放结束只用当前 ready 排版校准正式尺寸。
            if (resolution?.status !== 'ready') return false;

            const nextLayer = applySnapshotSize(layer, resolution.value);
            framework.service.frame.addOrUpdateLayer(nextLayer);

            return true;
        },
    };

    return behaviors;
};
